export const privateKeyCommand = `openssl genrsa -rand /dev/random -out private-key.pem 3072`;

export const certificateCommend = `openssl req -new -subj '/O=SIX Group AG/C=CH/CN=full-user-identifier' -key private-key.pem -out csr.pem`;

export const createCertificate = `openssl pkcs12 -export -in signed-certificate.pem -inkey private-key.pem -out certificate.p12`;

export const postmanOne = "https://api.six-group.com/web/v2/instruments/referenceData/instrumentBase?scheme=ISIN&ids=CH0009980894";

export const connectUsingPython = 
`"""
An example interface designed to be imported in your projects as a library.
"""
import urllib.request
import ssl
import json
from typing import List, Dict, Any

class APIError(Exception):
    def __init__(self, message: str, correlation_id: str = None):
        self.message = message
        self.correlation_id = correlation_id
        super().__init__(message)


class FinancialDataAPI:
    def __init__(self, certificate_path: str):
        self.url = 'https://api.six-group.com/web'
        self.headers = {
            "accept": "application/json"
        }
        self.context = ssl.create_default_context()
        self.context.load_cert_chain(f'{certificate_path}/signed-certificate.pem', f'{certificate_path}/private-key.pem')

    def _http_request(self, end_point: str, query_string: Dict[str, Any]) -> Dict[str, Any]:
        """
        Make an HTTP request and send the raw response.
        """
        complete_url = f"{self.url}{end_point}?{urllib.parse.urlencode(query_string)}"
        try:
            request = urllib.request.Request(complete_url, headers=self.headers)
            with urllib.request.urlopen(request, context=self.context) as response:
                return json.loads(response.read())
        except urllib.error.HTTPError as err:
            correlation_id = err.headers.get('X-CorrelationID')
            raise APIError("An error occurred during the API request.", correlation_id) from err

    def _http_request_with_scheme_id(self, end_point: str, scheme: str, ids: List[str]) -> Dict[str, Any]:
        """
        Make an HTTP request using scheme and ids.
        """
        query_string = {
            'scheme': scheme,
            'ids': ",".join(ids)
        }
        return self._http_request(end_point, query_string)

    def instrumentBase(self, scheme: str, instruments: List[str]) -> Dict[str, Any]:
        """
        Retrieve instrument basic attributes using scheme and ids.
        """
        end_point = "/v2/instruments/referenceData/instrumentBase"
        return self._http_request_with_scheme_id(end_point, scheme, instruments)

    def endOfDayHistory(self, scheme: str, listings: List[str], dateFrom: str, dateTo: str = '') -> Dict[str, Any]:
        """
        Retrieve End of Day Timeseries data.
        """
        end_point = "/v2/listings/marketData/endOfDayHistory"
        query_string = {
            'scheme': scheme,
            'ids': ",".join(listings),
            'dateFrom': dateFrom,
            'dateTo': dateTo
        }
        return self._http_request(end_point, query_string)


if __name__ == '__main__':
    findata = FinancialDataAPI('directory/with/private-key.pem and signed-certificate.pem')

    sample1 = findata.instrumentBase("ISIN", ["BE6342120662"])
    print(json.dumps(sample1, sort_keys=True, indent=4))`;

export const connectUsingJava = 
`import javax.net.ssl.KeyManagerFactory;
import javax.net.ssl.SSLContext;
import java.io.FileInputStream;
import java.io.IOException;
import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.security.GeneralSecurityException;
import java.security.KeyStore;
 
// Requires JDK 17 and no external libraries
public class RestJavaClient {
 
    private static final String URL = "https://api.six-group.com";
    private static final String CERTIFICATE = "C:/path/to//certificate.p12";
    private static final String PASSWORD = "your certificate.p12 password";
 
    public static void main(String[] args) throws Exception {
        // 1. create mTLS context
        SSLContext sslContext = createSslContextOrFail();
 
        // 2. create HTTP/2 client with mTLS setup
        HttpClient httpClient = HttpClient.newBuilder().version(HttpClient.Version.HTTP_2).sslContext(sslContext).build();
 
        // 3. create the request
        URI url = URI.create(URL + "/web/v2/instruments/referenceData/instrumentBase?ids=US0378331005&scheme=ISIN");
        HttpRequest request = HttpRequest.newBuilder()
            .uri(url)
            .build();
 
        // 4. send the request (blocking the current thread)
        HttpResponse<String> response = httpClient.send(request, HttpResponse.BodyHandlers.ofString());
 
        // 5. just show the response
        System.out.println(response.body());
    }
 
    private static SSLContext createSslContextOrFail() throws GeneralSecurityException, IOException {
        KeyStore store = KeyStore.getInstance("PKCS12");
        store.load(new FileInputStream(CERTIFICATE), PASSWORD.toCharArray());
        KeyManagerFactory keyManagerFactory = KeyManagerFactory.getInstance("SunX509");
        keyManagerFactory.init(store, PASSWORD.toCharArray());
        SSLContext sslContext = SSLContext.getInstance("TLSv1.2");
        sslContext.init(keyManagerFactory.getKeyManagers(), null, null);
        return sslContext;
    }
 
}`;

export const graphqlUrl = `https://api.six-group.com/web/v2/graphql`;

export const graphqlComposingQueries = 
`query InstrumentNameandStatus {
    instruments(
        scheme: ISIN
        ids: "CH0009980894"
    ) {
        lookup {
            instrumentShortName
            instrumentStatus
        }
    }
}`;

export const graphqlFlexibleQueries = 
`query InstrumentReferenceData {
    instruments(
        scheme: ISIN
        ids: "CH0009980894"
    ) {
        lookup {
            instrumentShortName
            instrumentStatus
        }
        referenceData {
            instrumentBase {            
                instrumentNamePrefix
                instrumentNameSuffix
                instrumentProductName
            }
        }
    }
}`;

export const graphqlGenerateQueryUrl = `https://api.six-group.com/web/v2/instruments/referenceData/instrumentBase?scheme=ISIN&ids=CH0009980894&extensions=EXPLAIN`;

export const graphqlGenerateQuery = 
`{
    "data": {
        // ... rest of the response ...
    },
    "extensions": {
        "explain": {
            "query": "query instrumentsBase($scheme: InstrumentScheme!, $ids: [UserInputId!]!, $preferredLanguage: Language) {
                instruments(scheme: $scheme, ids: $ids, preferredLanguage: $preferredLanguage) {
                    requestedId
                    requestedScheme
                    lookupStatus
                    lookup {
                        instrumentShortName
                        instrumentStatus
                        instrumentType
                    }
                    referenceData {
                        instrumentBase {
                            instrumentShortName
                            instrumentNamePrefix
                            instrumentNameSuffix
                            instrumentProductName
                            valor
                            isin
                            instrumentType
                            securityType
                            instrumentStatus
                            language
                            issuer {
                                shortName
                                longName
                                lei
                                gk
                            }
                            mostLiquidMarket {
                                shortName
                                longName
                                bcz
                                mic
                            }
                            nominalAmount
                            nominalCurrency
                            nominalPaidUp
                            instrumentUnitType
                            instrumentUnitSize
                            currentCouponRate
                            currentCouponType
                            maturityDate
                            maturityType
                            baseCurrency
                            tradingCurrency
                            baseCryptoSymbol
                            tradingCryptoSymbol
                            contractType
                            contractSymbol
                            underlyingInstrument {
                                shortName
                                valor
                                isin
                            }
                            ultimateUnderlyingInstrument {
                                shortName
                                valor
                                isin
                            }
                            optionType
                            optionStrikePrice
                            optionStrikePriceCurrency
                            exerciseType
                            expirationDate
                            contractSize
                            contractUnitType
                            contractMultiplier
                            sixVersionNumber
                        }
                    }
                }
            }",
            "variables": {
                "ids": [
                    "CH0009980894"
                ],
                "scheme": "ISIN"
            }
        }
    }
}`;

export const graphqlRequestInJava =
`import javax.net.ssl.KeyManagerFactory;
import javax.net.ssl.SSLContext;
import java.io.FileInputStream;
import java.io.IOException;
import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.security.GeneralSecurityException;
import java.security.KeyStore;
 
// Requires JDK 17 and no external libraries
public class GraphQLJavaClient {
 
    private static final String URL = "https://api.six-group.com/web/v2/graphql";
    private static final String CERTIFICATE = "C:/path/to//certificate.p12";
    private static final String PASSWORD = "your certificate.p12 password";
 
    public static void main(String[] args) throws Exception {
        // 1. create mTLS context
        SSLContext sslContext = createSslContextOrFail();
 
        // 2. create HTTP/2 client with mTLS setup
        HttpClient httpClient = HttpClient.newBuilder().version(HttpClient.Version.HTTP_2).sslContext(sslContext).build();
 
        // 3. create the request
        String query = """
            query Instrument {
                instruments(
                    scheme: ISIN
                    ids: ["CH0009980894"]
                ) {
                    requestedId
                    requestedScheme
                    lookupStatus
                    lookup {
                        instrumentShortName
                        instrumentStatus
                        instrumentType
                    }
                    referenceData {
                        instrumentBase {
                            instrumentShortName
                            instrumentNamePrefix
                            instrumentNameSuffix
                            instrumentProductName
                            valor
                            isin
                            instrumentType
                            securityType
                            instrumentStatus
                            language
                            issuer {
                                shortName
                                longName
                                lei
                                gk
                            }
                            mostLiquidMarket {
                                shortName
                                longName
                                bc
                                mic
                            }
                            nominalAmount
                            nominalCurrency
                            nominalPaidUp
                            instrumentUnitType
                            instrumentUnitSize
                            currentCouponRate
                            currentCouponType
                            maturityDate
                            maturityType
                            baseCurrency
                            tradingCurrency
                            baseCryptoSymbol
                            tradingCryptoSymbol
                            contractType
                            contractSymbol
                            underlyingInstrument {
                                shortName
                                valor
                                isin
                            }
                            ultimateUnderlyingInstrument {
                                shortName
                                valor
                                isin
                            }
                            optionType
                            optionStrikePrice
                            optionStrikePriceCurrency
                            exerciseType
                            expirationDate
                            contractSize
                            contractUnitType
                            contractMultiplier
                            sixVersionNumber
                        }
                    }
                }
            }
            """;
        HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(URL))
                .header("Content-Type", "application/graphql")
                .POST(HttpRequest.BodyPublishers.ofString(query))
                .build();
 
        // 4. send the request (blocking the current thread)
        HttpResponse<String> response = httpClient.send(request, HttpResponse.BodyHandlers.ofString());
 
        // 5. just show the response
        System.out.println(response.body());
    }
 
    private static SSLContext createSslContextOrFail() throws GeneralSecurityException, IOException {
        KeyStore store = KeyStore.getInstance("PKCS12");
        store.load(new FileInputStream(CERTIFICATE), PASSWORD.toCharArray());
        KeyManagerFactory keyManagerFactory = KeyManagerFactory.getInstance("SunX509");
        keyManagerFactory.init(store, PASSWORD.toCharArray());
        SSLContext sslContext = SSLContext.getInstance("TLSv1.2");
        sslContext.init(keyManagerFactory.getKeyManagers(), null, null);
        return sslContext;
    }
}`;

export const graphqlRequestInPython =
`import requests
import json
 
cert = ('path to certificate/signed-certificate.pem', 'path to certificate/private-key.pem')
 
url = 'https://api.six-group.com/web/v2/graphql'
 
# Define the headers for the request
headers = {
    'Content-Type': 'application/json',
}
 
# Define the query and variables
query = """
    query Instruments($scheme: InstrumentScheme!, $ids: [UserInputId!]!, $preferredLanguage: Language) {
        instruments(scheme: $scheme, ids: $ids, preferredLanguage: $preferredLanguage) {
            requestedId
            requestedScheme
            lookupStatus
            lookup {
                instrumentShortName
                instrumentStatus
                instrumentType
            }
            referenceData {
                instrumentBase {
                    instrumentShortName
                    instrumentNamePrefix
                    instrumentNameSuffix
                    instrumentProductName
                    valor
                    isin
                    instrumentType
                    securityType
                    instrumentStatus
                    language
                    issuer {
                        shortName
                        longName
                        lei
                        gk
                    }
                    mostLiquidMarket {
                        shortName
                        longName
                        bc
                        mic
                    }
                    nominalAmount
                    nominalCurrency
                    nominalPaidUp
                    instrumentUnitType
                    instrumentUnitSize
                    currentCouponRate
                    currentCouponType
                    maturityDate
                    maturityType
                    baseCurrency
                    tradingCurrency
                    baseCryptoSymbol
                    tradingCryptoSymbol
                    contractType
                    contractSymbol
                    underlyingInstrument {
                        shortName
                        valor
                        isin
                    }
                    ultimateUnderlyingInstrument {
                        shortName
                        valor
                        isin
                    }
                    optionType
                    optionStrikePrice
                    optionStrikePriceCurrency
                    exerciseType
                    expirationDate
                    contractSize
                    contractUnitType
                    contractMultiplier
                    sixVersionNumber
                }
            }
        }
    }
"""
variables = {
    "ids": ["CH0009980894", "IE00B5BMR087", "CH0559601544", "US0378331005", "JE00B1VS3770", "US037833AK68", "XXX"],
    "scheme": "ISIN"
} 
 
response = requests.post(url, headers=headers, json={'query': query, 'variables': variables}, cert=cert)
 
pretty_r = json.dumps(json.loads(response.content),indent=2)
 
print(pretty_r)
 
# ensure the request was successful
if response.status_code == 200:
    print(response.json())
else:
    print(f"Request failed with status code {response.status_code}")
`;

export const apiVersioning =
`'https://api.six-group.com/web/v2/instruments/referenceData/instrumentBase?scheme=ISIN&ids=CH0009980894%2CIE00B5BMR087%2CCH0559601544%2CUS0378331005%2CJE00B1VS3770%2CUS037833AK68%2CXXX&preferredLanguage=EN' \
  -H 'accept: application/json' \
  -H 'api-version: 2025-06-01'`;

export const majorVersionV2VersionUrl1 = `https://api.six-group.com/web/v1/`;

export const majorVersionV2VersionUrl2 = `https://api.six-group.com/web/v2/`;

export const majorVersionV3VersionUrl3 = `https://api.six-group.com/web/v3/`;

export const majorVersionV2ErrorMessageExample =
`"errors": [
  {
    "category": "ENTITLEMENT_ERROR",
    "type": "ACCESS_DENIED",
    "messageCode": 3001,
    "message": "User does not have access to the requested quality of service: 'REAL_TIME'. Entitled quality of service: 'END_OF_DAY'. Contact SIX for permissions.",
    "messageValues": {
      "requestedQualityOfService": "REAL_TIME",
      "entitledQualityOfService": "END_OF_DAY"
    }
  }
]`;

export const majorVersionV2V1Definition =
`"""
A close stream message. Either the stream has been found and stopped or not found by the given ID.
"""
type CloseStream {
    streamId: ID!
    type: MessageType!
    message: String!
    """(DEPRECATED)"""
    requestedId: UserInputId @deprecated(reason: "will be removed")
    """(DEPRECATED)"""
    requestedScheme: ListingScheme @deprecated(reason: "will be removed")
}`;

export const majorVersionV2V2Definition =
`"""
A close stream message. Either the stream has been found and stopped or not found by the given ID.
"""
type CloseStream {
    streamId: ID!
    type: MessageType!
    message: String!
}`;

export const majorVersionV2V1ParameterDefinition =
`@RequestParam(value = "priceAdjustment", required = false)
@Schema(
    description = "ADJUSTED = Prices are after the occurrence of corporate action events historically adjusted for the requested period."
                  "UNADJUSTED = Prices are not adjusted."
                  "ADJUSTED_AND_DIVIDEND_ADJUSTED = Prices are after the occurrence of corporate action events historically and dividend-adjusted for the requested period.",
    allowableValues = {"UNADJUSTED", "ADJUSTED", "ADJUSTED_AND_DIVIDEND_ADJUSTED"},
    defaultValue = "ADJUSTED",
    example = "ADJUSTED"
)
EndOfDayPriceAdjustment priceAdjustment;`;

export const majorVersionV2V2ParameterDefinition =
`@RequestParam(value = "priceAdjustment", required = false)
@Schema(
    description = "Specifies whether prices are adjusted for corporate actions (e.g., stock splits) and/or dividends. By default, prices are adjusted backwards for corporate actions excluding dividends affecting the price.",
    allowableValues = {"ADJUSTED", "UNADJUSTED", "DIVIDEND_ADJUSTED_FORWARD", "DIVIDEND_ADJUSTED_BACKWARD"},
    defaultValue = "ADJUSTED",
    example = "ADJUSTED"
)
EndOfDayPriceAdjustment priceAdjustment;`;

export const errorCodesExample =
`"errors": [
  {
    "category": "ENTITLEMENT_ERROR",
    "type": "ACCESS_DENIED",
    "messageCode": 3001,
    "message": "User does not have access to the requested quality of service: 'REAL_TIME'. Entitled quality of service: 'END_OF_DAY'. Contact SIX for permissions.",
    "messageValues": {
      "requestedQualityOfService": "REAL_TIME",
      "entitledQualityOfService": "END_OF_DAY"
    }
  }
]`;
